import { BUG_REPORT_LIMITS, type BugReportScreenshot } from '@sorrem/shared';

/** JPEG quality for a screenshot: small enough to send, sharp enough to read the HUD. */
const SCREENSHOT_JPEG_QUALITY = 0.8;

/** Chrome's tab-capture options, which the DOM typings don't all carry yet. */
interface TabCaptureOptions extends DisplayMediaStreamOptions {
    /** Offers the current tab in a simplified prompt. */
    preferCurrentTab?: boolean;
    selfBrowserSurface?: 'include' | 'exclude';
    surfaceSwitching?: 'include' | 'exclude';
}

/** A captured tab: what is sent, and a URL to preview it in the form. */
export interface TabScreenshot {
    readonly screenshot: BugReportScreenshot;
    /** An object URL for the JPEG; revoke it when the preview goes away. */
    readonly previewUrl: string;
}

/**
 * Captures one frame of the current tab with Chrome's tab capture (#235).
 * Chrome asks the tester first, every time; resolves to null when they
 * decline. Call it from a click, since the prompt needs user activation, and
 * hide anything that shouldn't be in the picture before calling.
 */
export async function captureTabScreenshot(): Promise<TabScreenshot | null> {
    const stream = await requestTabCaptureStream();
    if (!stream) {
        return null;
    }

    try {
        const blob = await grabFrame(stream);
        return {
            screenshot: { mimeType: 'image/jpeg', dataBase64: await toBase64(blob) },
            previewUrl: URL.createObjectURL(blob),
        };
    } finally {
        stream.getTracks().forEach((track) => track.stop());
    }
}

/**
 * Asks Chrome to share the current tab, offering it first in the prompt.
 * Resolves to null when the user declines. Needs user activation (a click
 * or a key press).
 */
export async function requestTabCaptureStream(): Promise<MediaStream | null> {
    const options: TabCaptureOptions = {
        video: { displaySurface: 'browser' },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        surfaceSwitching: 'exclude',
    };
    try {
        return await navigator.mediaDevices.getDisplayMedia(options);
    } catch (error) {
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
            return null;
        }
        throw error;
    }
}

/** The stream's first frame as a JPEG, scaled so its longest side fits the limit. */
async function grabFrame(stream: MediaStream): Promise<Blob> {
    const video = document.createElement('video');
    video.muted = true;
    video.srcObject = stream;
    await video.play();
    // Wait for a frame to reach the compositor, so the capture isn't blank.
    await new Promise<void>((resolveFrame) => video.requestVideoFrameCallback(() => resolveFrame()));

    const scale = Math.min(1, BUG_REPORT_LIMITS.maxScreenshotDimension / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
    video.srcObject = null;

    const blob = await new Promise<Blob | null>((resolveBlob) => canvas.toBlob(resolveBlob, 'image/jpeg', SCREENSHOT_JPEG_QUALITY));
    if (!blob) {
        throw new Error('The screenshot could not be encoded.');
    }
    if (blob.size > BUG_REPORT_LIMITS.maxScreenshotBytes) {
        throw new Error('The screenshot is too large to send.');
    }
    return blob;
}

/** The blob's bytes as plain base64, without the data URL prefix. */
function toBase64(blob: Blob): Promise<string> {
    return new Promise((resolveText, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolveText(String(reader.result).slice(String(reader.result).indexOf(',') + 1));
        reader.onerror = () => reject(reader.error ?? new Error('The screenshot could not be read.'));
        reader.readAsDataURL(blob);
    });
}
